(() => {
  const cache = new Map();
  const versions = new WeakMap();

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

  const escape = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);

  async function load(id, country) {
    const key = `${country}:${id}`;

    if (cache.has(key)) {
      return cache.get(key);
    }

    const promise = (async () => {
      const components = [];
      let after = null;

      do {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal: AbortSignal.timeout(10000),
          body: JSON.stringify({
            query: `
              query BundleContents(
                $id: ID!,
                $after: String,
                $country: CountryCode
              ) @inContext(country: $country) {
                node(id: $id) {
                  ... on ProductVariant {
                    components(first: 50, after: $after) {
                      nodes {
                        quantity

                        productVariant {
                          id
                          title
                          sku

                          rollsPerCase: metafield(
                            namespace: "custom",
                            key: "rolls_per_case"
                          ) {
                            value
                          }

                          image {
                            url(
                              transform: {
                                maxWidth: 160
                              }
                            )
                          }

                          product {
                            title
                            handle
                          }
                        }
                      }

                      pageInfo {
                        hasNextPage
                        endCursor
                      }
                    }
                  }
                }
              }
            `,
            variables: {
              id: `gid://shopify/ProductVariant/${id}`,
              country,
              after
            }
          })
        });

        if (!response.ok) {
          throw new Error(`Bundle lookup failed: ${response.status}`);
        }

        const result = await response.json();

        const connection = result.data?.node?.components;

        if (!connection) {
          console.error('Bundle GraphQL errors:', result.errors);
          throw new Error('Bundle contents unavailable');
        }

        if (result.errors?.length) {
          console.warn('Bundle GraphQL warnings:', result.errors);
        }

        components.push(...connection.nodes);

        after = connection.pageInfo.hasNextPage
          ? connection.pageInfo.endCursor
          : null;

      } while (after);

      return components;
    })();

    cache.set(key, promise);

    try {
      return await promise;
    } catch (error) {
      cache.delete(key);
      throw error;
    }
  }

  function render(components) {
    return `
      <ul class="bundle-includes__list">
        ${components.map(({ quantity, productVariant: variant }) => {
          const variantId = variant.id.split('/').pop();

          const url = escape(
            `${root}products/${encodeURIComponent(variant.product.handle)}?variant=${variantId}`
          );

          const variantTitle =
            variant.title === 'Default Title'
              ? 'Each'
              : escape(variant.title);

          const rollsPerCase = variant.rollsPerCase?.value
            ? ` · ${escape(variant.rollsPerCase.value)} rolls`
            : '';

          return `
            <li class="bundle-includes__item">

              ${
                variant.image
                  ? `
                    <a
                      class="bundle-includes__image"
                      href="${url}"
                      tabindex="-1"
                      aria-hidden="true"
                    >
                      <img
                        src="${escape(variant.image.url)}"
                        alt=""
                        width="72"
                        height="72"
                        loading="lazy"
                      >
                    </a>
                  `
                  : ''
              }

              <div class="bundle-includes__details">

                <a
                  class="bundle-includes__title"
                  href="${url}"
                >
                  ${escape(variant.product.title)}
                </a>

                <p class="bundle-includes__variant">
                  <strong>${escape(quantity)} ×</strong>
                  ${variantTitle}${rollsPerCase}
                </p>

                ${
                  variant.sku
                    ? `
                      <p class="bundle-includes__sku">
                        SKU: ${escape(variant.sku)}
                      </p>
                    `
                    : ''
                }

              </div>
            </li>
          `;
        }).join('')}
      </ul>
    `;
  }

  async function update(element, id) {
    const version = (versions.get(element) || 0) + 1;

    versions.set(element, version);

    element.dataset.variantId = id || '';

    const target = element.querySelector('[data-bundle-components]');

    if (!target) {
      return;
    }

    target.setAttribute('aria-busy', 'true');

    target.innerHTML = '<p>Loading bundle contents…</p>';

    try {
      if (!id) {
        target.innerHTML =
          '<p>Select an option to see what’s included.</p>';
        return;
      }

      const components = await load(
        id,
        element.dataset.country
      );

      if (versions.get(element) !== version) {
        return;
      }

      target.innerHTML = components.length
        ? render(components)
        : '<p>See the product description for bundle contents.</p>';

    } catch (error) {
      console.error('Bundle includes error:', error);

      if (versions.get(element) !== version) {
        return;
      }

      target.innerHTML = `
        <p>Could not load bundle contents.</p>
        <button
          type="button"
          data-bundle-retry
        >
          Try again
        </button>
      `;

    } finally {
      if (versions.get(element) === version) {
        target.setAttribute('aria-busy', 'false');
      }
    }
  }

  function init(scope = document) {
    scope
      .querySelectorAll('[data-bundle-includes]')
      .forEach((element) => {

        if (element.dataset.initialized) {
          return;
        }

        element.dataset.initialized = 'true';

        element.addEventListener('click', (event) => {
          if (
            event.target.closest('[data-bundle-retry]')
          ) {
            update(
              element,
              element.dataset.variantId
            );
          }
        });

        update(
          element,
          element.dataset.variantId
        );
      });
  }

  // Empire variant-change event does not bubble normally,
  // so listen during capture.
  document.addEventListener(
    'variant-change',
    (event) => {
      const product = event.detail?.product;

      if (!product) {
        return;
      }

      document
        .querySelectorAll('[data-bundle-includes]')
        .forEach((element) => {

          if (
            element.dataset.productId ===
            String(product.id)
          ) {
            update(
              element,
              event.detail.variant?.id
            );
          }
        });
    },
    true
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init()
    );
  } else {
    init();
  }
})();